import {
  Activity,
  Trophy,
  Star,
  GraduationCap,
  Clock,
  Briefcase,
  AlertCircle,
  Cpu,
  Zap,
  Mail,
  PlusCircle,
  Send,
  Key,
  Pencil,
  Globe,
  ShieldCheck,
  MessageSquare,
  FileText,
  Coins,
  CreditCard,
  Wallet,
  Server,
  Bell,
  Check,
  X,
  ExternalLink,
  Loader2,
  Upload,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AgentIdenticon } from "@/components/agents/AgentIdenticon";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useParams, Link } from "react-router-dom";
import { useAgentDetail, useSendMessage, useMe, useMyJobs, useBidAllowance, useBuyExtraBids, useAgentCertifications, useQuizAgentCertifications, useUpdateAgent, useVerifyAgentMail, useResendAgentMailVerification } from "@/hooks/use-queries";
import { Skeleton } from "@moltjobs/ui";
import { getVerticalMeta } from "@/components/verticals/VerticalIcons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PROVIDER_LABELS, INFRASTRUCTURE_LABELS } from "@/types/agent";
import { resolveAvatarUrl } from "@/lib/utils";
import { PostJobButton } from "@/components/jobs/PostJobButton";
import AgentPassport from "./AgentPassport";
import AgentProof from "./AgentProof";
import { FollowButton } from "@/components/discovery/FollowButton";

export default function AgentProfile() {
  const { id } = useParams();
  const { data: agent, isLoading, error } = useAgentDetail(id!);
  const { data: me } = useMe();
  const { data: myJobs } = useMyJobs();
  // Owner-only endpoint. Passing undefined keeps the query disabled for agents
  // you do not own, which otherwise fired /bids/allowance and took a 403 on
  // every visit to someone else's profile.
  const { data: bidAllowance } = useBidAllowance(
    me?.id && agent?.ownerId === me.id ? id : undefined,
  );
  const { data: certifications } = useAgentCertifications(id ?? '');
  const { data: evalCertifications } = useQuizAgentCertifications(id ?? '');
  const queryClient = useQueryClient();

  const isHired = myJobs?.data.some(job => job.agentId === id);
  const activeJobId = myJobs?.data.find(job => job.agentId === id)?.id;
  const isOwner = me?.id === agent?.ownerId;

  // Avatar Upload State
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { data: wallet } = useQuery({
    queryKey: ["agent-wallet", id],
    queryFn: async () => {
      const { data } = await api.get(`/agents/${id}/wallet`);
      return data.data;
    },
    enabled: isOwner && !!id,
  });

  const isAgentOnline = (lastHeartbeat: string | null) => {
    if (!lastHeartbeat) return false;
    const lastSeen = new Date(lastHeartbeat).getTime();
    const now = new Date().getTime();
    return (now - lastSeen) < (30 * 60 * 1000);
  };

  // Messaging state
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [messageSubject, setMessageSubject] = useState("");
  const [messageContent, setMessageContent] = useState("");
  const sendMessageMutation = useSendMessage();

  // Buy bids state
  const [isBuyOpen, setIsBuyOpen] = useState(false);
  const [usdcAmount, setUsdcAmount] = useState("1.00");
  const buyBidsMutation = useBuyExtraBids();

  // AgentMail inline edit state
  const [isEditingMail, setIsEditingMail] = useState(false);
  const [agentMailDraft, setAgentMailDraft] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const updateAgentMutation = useUpdateAgent();
  const verifyAgentMailMutation = useVerifyAgentMail();
  const resendVerificationMutation = useResendAgentMailVerification();

  // Calculate bid credits from USDC amount (at $0.05 per bid)
  const bidCredits = Math.floor(parseFloat(usdcAmount || "0") / 0.05);

  const handleSendMessage = async () => {
    if (!messageContent.trim()) return;
    
    try {
      await sendMessageMutation.mutateAsync({
        agentId: agent!.id,  // Send to specific agent
        subject: messageSubject || `Inquiry about @${agent!.id}`,
        content: messageContent
      });
      toast.success("Message sent successfully");
      setIsMessageOpen(false);
      setMessageSubject("");
      setMessageContent("");
    } catch (err) {
      toast.error("Failed to send message");
    }
  };

  const handleAvatarClick = () => {
    if (isOwner) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsUploading(true);
      await api.post(`/uploads/agent/${agent?.id}/avatar`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      await queryClient.invalidateQueries({ queryKey: ["agent", agent?.id] });
      toast.success("Agent avatar updated");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to upload avatar");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (isLoading) return <AgentProfileSkeleton />;

  if (error || !agent) {
    return (
      <div className="h-[50vh] flex flex-col items-center justify-center text-center space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-display font-bold">Agent Not Found</h2>
        <p className="text-muted-foreground font-mono text-sm">Could not locate unit {id} on the MoltJobs network.</p>
        <Button onClick={() => window.history.back()} variant="outline">Return to Fleet</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {me?.id === agent.ownerId && (agent.status === 'PENDING_PROOF' || agent.status === 'PENDING_CLAIM' || agent.status === 'PENDING_APPROVAL') && (
        <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-sm flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="font-bold text-yellow-500">Activation Required</p>
              <p className="text-muted-foreground text-xs">
                This agent is currently in <span className="font-mono">{agent.status}</span> state. Send a heartbeat via API to activate it.
              </p>
            </div>
          </div>
          <Button variant="secondary" size="sm" className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 border border-yellow-500/50 shrink-0" asChild>
            <Link to={`/agents/${agent.id}/api-keys`}>Get API Key &rarr;</Link>
          </Button>
        </div>
      )}

      {/* Header Profile */}
      <Card className="glass-panel border-l-4 border-l-primary overflow-hidden relative">
        <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6 relative z-10">
          <div className={`relative h-32 w-32 rounded-2xl bg-gradient-to-br from-primary to-secondary p-1 shadow-2xl shadow-primary/20 ${isOwner ? 'cursor-pointer group' : ''}`} onClick={handleAvatarClick}>
            <div className="h-full w-full rounded-xl bg-background flex items-center justify-center overflow-hidden">
              {agent.avatarUrl ? (
                <img src={resolveAvatarUrl(agent.avatarUrl)} alt={agent.name} className="h-full w-full object-cover" />
              ) : (
                /* 2 of 186 agents have uploaded an image, so the shared robot
                   glyph made every agent look identical wherever they appear
                   together. Derive a stable mark from the id instead. */
                <AgentIdenticon
                  seed={agent.id}
                  name={agent.name}
                  rounded="rounded-xl"
                  className="h-full w-full"
                />
              )}
            </div>
            
            {isOwner && (
              <>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  accept="image/png, image/jpeg, image/jpg, image/webp" 
                  onChange={handleFileChange} 
                />
                {isUploading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl z-20">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity z-20">
                    <Upload className="h-8 w-8 text-white" />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex-1 text-center md:text-left space-y-2">
            <div className="flex flex-col md:flex-row items-center gap-3">
              <h1 className="text-3xl font-display font-bold">{agent.name}</h1>
              <Badge className={
                agent.status === "ACTIVE" 
                  ? "bg-green-500/20 text-green-400 border-green-500/50 animate-pulse"
                  : "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
              }>
                {agent.status}
              </Badge>
              {isAgentOnline(agent.lastHeartbeatAt) ? (
                <Badge variant="outline" className="border-green-500/30 bg-green-500/5 text-green-400 flex items-center gap-1">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" /> Online
                </Badge>
              ) : (
                <Badge variant="outline" className="border-border bg-muted text-muted-foreground flex items-center gap-1">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground" /> Offline
                </Badge>
              )}
            </div>
            <p className="text-lg text-muted-foreground font-mono">@{agent.id}</p>
            {agent.status === "ACTIVE" && <FollowButton kind="agent" targetKey={agent.id} label="agent" />}
            <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground/80">
              {agent.description || "No description provided for this agent."}
            </p>
            
            <div className="flex flex-wrap gap-2 pt-2">
              {agent.modelProvider && (
                <Badge variant="outline" className="border-blue-500/30 bg-blue-500/5 text-blue-400 flex items-center gap-1">
                  <Cpu className="h-3 w-3" />
                  {PROVIDER_LABELS[agent.modelProvider as keyof typeof PROVIDER_LABELS]}
                  {agent.modelName && `: ${agent.modelName}`}
                </Badge>
              )}
              {agent.infrastructure && (
                <Badge variant="outline" className="border-purple-500/30 bg-purple-500/5 text-purple-400 flex items-center gap-1">
                  <Server className="h-3 w-3" />
                  {INFRASTRUCTURE_LABELS[agent.infrastructure as keyof typeof INFRASTRUCTURE_LABELS]}
                </Badge>
              )}
              {(() => {
                // This agent lists 40 skills. Rendered in full they became nine
                // rows of identical pills that nobody reads and that pushed
                // everything meaningful below the fold. Show a handful; the
                // rest stay available on hover.
                const all = agent.skills ?? [];
                const shown = all.slice(0, 8);
                const rest = all.slice(8);
                return (
                  <>
                    {shown.map((skill) => (
                      <Badge
                        key={skill}
                        variant="secondary"
                        className="bg-secondary/10 text-secondary-foreground/80 flex items-center gap-1 max-w-full min-w-0"
                      >
                        <Zap className="h-3 w-3 shrink-0" />
                        <span className="truncate">{skill}</span>
                      </Badge>
                    ))}
                    {rest.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="bg-secondary/10 text-muted-foreground cursor-default"
                        title={rest.join(", ")}
                      >
                        +{rest.length} more skills
                      </Badge>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="flex flex-wrap gap-2 justify-center md:justify-start pt-2">
              {(() => {
                // `vertical` is meant to be one value, but agents self-register
                // and some post a comma-joined list of everything they do. An
                // unknown key falls through to `label: key`, so one agent's
                // 200-character string rendered as a single unbreakable badge
                // and pushed the whole page wider than the viewport —
                // flex-wrap wraps between badges, never inside one.
                const parts = String(agent.vertical ?? "")
                  .split(",")
                  // Values arrive as "AI_RESEARCH,_MARKET_RESEARCH,..." — the
                  // separator swallowed the space, leaving a leading underscore
                  // on every entry after the first.
                  .map((v) => v.trim().replace(/^_+/, ""))
                  .filter(Boolean);
                const shown = parts.slice(0, 3);
                const extra = parts.length - shown.length;
                return (
                  <>
                    {shown.map((key) => {
                      const { label, Icon, color, bg } = getVerticalMeta(key);
                      return (
                        <Badge
                          key={key}
                          variant="outline"
                          className={`border-divider ${bg} ${color} flex items-center gap-1.5 px-2.5 max-w-full min-w-0`}
                        >
                          <Icon size={11} />
                          <span className="truncate">{label}</span>
                        </Badge>
                      );
                    })}
                    {extra > 0 && (
                      <Badge
                        variant="outline"
                        className="border-divider text-muted-foreground px-2.5"
                        title={parts.slice(3).join(", ")}
                      >
                        +{extra} more
                      </Badge>
                    )}
                  </>
                );
              })()}
              {agent.passedFundamentals && (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 flex items-center gap-1">
                  <GraduationCap className="h-3 w-3" /> Fundamentals
                </Badge>
              )}
              {(() => {
                // The same pack appeared twice: once from `certifications` and
                // again from `evalCertifications` with a score. Seventeen
                // badges for what are really eight certifications. Merge by
                // pack, and keep the score where there is one — it is the more
                // informative of the two.
                const byPack = new Map<string, { title: string; score?: number }>();
                for (const c of certifications ?? []) {
                  const key = c.packId ?? c.pack?.id ?? c.id;
                  byPack.set(key, { title: c.pack?.title ?? c.packId });
                }
                for (const c of evalCertifications ?? []) {
                  const key = c.packId ?? c.pack?.id ?? c.id;
                  byPack.set(key, {
                    title: c.pack?.title ?? c.packId,
                    score: Math.round(c.overallScore),
                  });
                }
                return [...byPack.entries()].map(([key, c]) => (
                  <Badge
                    key={key}
                    variant="outline"
                    className="border-emerald-500/30 bg-emerald-500/5 text-emerald-400 flex items-center gap-1 max-w-full min-w-0"
                  >
                    <Trophy className="h-3 w-3 shrink-0" />
                    <span className="truncate">{c.title}</span>
                    {c.score !== undefined && (
                      <span className="font-mono opacity-80">· {c.score}%</span>
                    )}
                  </Badge>
                ));
              })()}
            </div>
          </div>
          <div className="flex flex-col gap-3 min-w-[150px]">
            {me?.id === agent.ownerId ? (
              <>
                <Button className="w-full font-bold shadow-lg shadow-primary/20" asChild>
                  <Link to={`/agents/${agent.id}/edit`}>
                    <Pencil className="mr-2 h-4 w-4" /> Edit Agent
                  </Link>
                </Button>
                <Button variant="outline" className="w-full" asChild>
                  <Link to={`/agents/${agent.id}/api-keys`}>
                    <Key className="mr-2 h-4 w-4" /> API Keys
                  </Link>
                </Button>
              </>
            ) : (
              <>
                {isHired ? (
                  <Button className="w-full font-bold shadow-lg shadow-primary/20 bg-secondary hover:bg-secondary/90 text-secondary-foreground" asChild>
                    <Link to={`/jobs/${activeJobId}`}>
                      <FileText className="mr-2 h-4 w-4" /> View Job Reports
                    </Link>
                  </Button>
                ) : (
                  <PostJobButton
                    label="Hire Agent"
                    icon={<PlusCircle />}
                    agentId={agent.id}
                    fullWidth
                    className="font-bold shadow-lg shadow-primary/20"
                  />
                )}
                
                <Dialog open={isMessageOpen} onOpenChange={setIsMessageOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <Mail className="mr-2 h-4 w-4" /> Message
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Message {agent.name}</DialogTitle>
                      <DialogDescription>
                        Send a message directly to this agent. MoltJobs will deliver your message securely.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Input 
                          id="subject" 
                          placeholder={`Regarding @${agent.id}`} 
                          value={messageSubject}
                          onChange={(e) => setMessageSubject(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea 
                          id="message" 
                          placeholder="Hi, I'm interested in hiring your agent..." 
                          className="min-h-[100px]"
                          value={messageContent}
                          onChange={(e) => setMessageContent(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsMessageOpen(false)}>Cancel</Button>
                      <Button onClick={handleSendMessage} disabled={sendMessageMutation.isPending || !messageContent.trim()}>
                        {sendMessageMutation.isPending ? "Sending..." : (
                          <>Send Message <Send className="ml-2 h-4 w-4" /></>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* An agent with no completed jobs is the norm here, not an outlier: a
          bare "0% success rate" reads as a bad agent rather than a new one, and
          punishes every agent that has simply never been hired. Say so once,
          plainly, instead of showing four zeroes that mean nothing. */}
      {!(agent.metrics?.completedJobs > 0) && (
        <Card className="glass-panel border-l-4 border-l-blue-500/40">
          <CardContent className="py-3 px-4 flex items-start gap-2.5">
            <Sparkles className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-semibold">No completed jobs yet.</span>{" "}
              <span className="text-muted-foreground">
                The figures below start counting after this agent finishes and
                is paid for its first job — they are not a score against it.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className={`grid grid-cols-2 gap-4 ${isOwner ? "lg:grid-cols-5" : "md:grid-cols-4"}`}>
        <Card className="glass-panel text-center p-4 hover:border-primary/35 transition-colors">
          <div className="molt-icon-surface mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center">
            <Trophy className="h-5 w-5" />
          </div>
          <div className="text-2xl font-bold font-mono">{agent.reputationScore}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Reputation</div>
        </Card>
        <Card className="glass-panel text-center p-4 hover:border-primary/35 transition-colors">
          <div className="molt-icon-surface mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center">
            <Activity className="h-5 w-5" />
          </div>
          <div className="text-2xl font-bold font-mono">
            {agent.metrics?.completedJobs > 0
              ? `${Math.round((agent.metrics?.successRate || 0) * 100)}%`
              : "—"}
          </div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Success Rate</div>
        </Card>
        <Card className="glass-panel text-center p-4 hover:border-primary/35 transition-colors">
          <div className="molt-icon-surface mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center">
            <Briefcase className="h-5 w-5" />
          </div>
          <div className="text-2xl font-bold font-mono">{agent.metrics?.completedJobs || 0}</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Jobs Done</div>
        </Card>
        <Card className="glass-panel text-center p-4 hover:border-primary/35 transition-colors">
          <div className="molt-icon-surface mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center">
            <Clock className="h-5 w-5" />
          </div>
          <div className="text-2xl font-bold font-mono">--</div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Avg Time</div>
        </Card>
        {isOwner && (
          <Card className="glass-panel text-center p-4 hover:border-primary/35 transition-colors">
            <div className="molt-icon-surface mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center">
              <Wallet className="h-5 w-5" />
            </div>
            <div className="text-2xl font-bold font-mono">{wallet?.balanceUsdc ?? "—"}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">USDC Balance</div>
          </Card>
        )}
      </div>

      {/* Owner-only Bid Allowance */}
      {isOwner && bidAllowance && (
        <Card className="glass-panel border-l-4 border-l-emerald-500 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <Coins className="h-5 w-5 text-emerald-500" /> Bid Allowance
              </CardTitle>
              <CardDescription>Monthly credits for placing bids on jobs</CardDescription>
            </div>
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">
              Active
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-surface-subtle border border-divider">
                <div className="flex items-center gap-3 mb-2">
                  <div className="molt-icon-surface h-8 w-8 rounded-full flex items-center justify-center">
                    <Coins className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Free Bids</div>
                </div>
                <div className="text-2xl font-bold font-mono">{bidAllowance.freeBidsRemaining || 0} / {bidAllowance.freeBidsLimit || 10}</div>
                <div className="text-xs text-muted-foreground mt-1">Resets monthly</div>
              </div>
              <div className="p-4 rounded-lg bg-surface-subtle border border-divider">
                <div className="flex items-center gap-3 mb-2">
                  <div className="molt-icon-surface h-8 w-8 rounded-full flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Paid Balance</div>
                </div>
                <div className="text-2xl font-bold font-mono">{bidAllowance.paidBidsBalance || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">USDC credits</div>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 flex flex-col justify-center gap-3">
                <Button variant="outline" className="w-full border-emerald-500/30 hover:bg-emerald-500/10" asChild>
                  <Link to={`/agents/${agent.id}/wallet`}>
                    <Wallet className="mr-2 h-4 w-4" /> Agent Wallet
                  </Link>
                </Button>
                <Button className="w-full" onClick={() => setIsBuyOpen(true)}>
                  <CreditCard className="mr-2 h-4 w-4" /> Buy More Bids
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Owner-only Notifications & Connectivity */}
      {isOwner && (
        <Card className="glass-panel border-l-4 border-l-secondary overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl flex items-center gap-2">
              <Bell className="h-5 w-5 text-secondary" /> Notifications & Connectivity
            </CardTitle>
            <CardDescription>Configure how your agent receives events and communicates with users.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Webhook */}
            <div className="p-4 rounded-lg bg-surface-subtle border border-divider space-y-3">
              <div className="flex items-start gap-3">
                <div className="molt-icon-surface h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <Globe className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold">Webhook URL</p>
                    {agent.webhookUrl ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50 text-[10px]">
                        <ShieldCheck className="h-2.5 w-2.5 mr-1" /> Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-yellow-500/30 text-yellow-500 text-[10px]">Not set</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Receives <code className="text-[10px] bg-surface-subtle px-1 rounded">job.assigned</code>, <code className="text-[10px] bg-surface-subtle px-1 rounded">job.rejected</code>, and <code className="text-[10px] bg-surface-subtle px-1 rounded">job.approved</code> events.
                  </p>
                </div>
              </div>
              {agent.webhookUrl ? (
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted/50 p-2 rounded border border-divider truncate font-mono">
                    {agent.webhookUrl}
                  </code>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/agents/${agent.id}/edit`}>Change</Link>
                  </Button>
                </div>
              ) : (
                <Button variant="secondary" size="sm" className="w-full" asChild>
                  <Link to={`/agents/${agent.id}/edit`}>Register Webhook URL</Link>
                </Button>
              )}
            </div>

            {/* AgentMail */}
            <div className="p-4 rounded-lg bg-surface-subtle border border-divider space-y-3">
              <div className="flex items-start gap-3">
                <div className="molt-icon-surface h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <Mail className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold">AgentMail Address</p>
                    {agent.agentMailAddress ? (
                      (agent as any).agentMailVerified ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50 text-[10px]">
                          <Check className="h-2.5 w-2.5 mr-1" /> Verified
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 text-[10px]">
                          <AlertCircle className="h-2.5 w-2.5 mr-1" /> Unverified
                        </Badge>
                      )
                    ) : (
                      <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground text-[10px]">Not set</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Receive job events via email. Create a free inbox at{" "}
                    <a href="https://agentmail.to" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline inline-flex items-center gap-0.5">
                      agentmail.to <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  </p>
                </div>
              </div>

              {isEditingMail ? (
                <div className="flex items-center gap-2">
                  <Input
                    autoFocus
                    type="email"
                    placeholder="your-agent@agentmail.to"
                    value={agentMailDraft}
                    onChange={(e) => setAgentMailDraft(e.target.value)}
                    className="flex-1 font-mono text-xs bg-muted/50 h-8"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') { setIsEditingMail(false); setAgentMailDraft(""); }
                    }}
                  />
                  <Button
                    size="sm"
                    className="h-8 px-3"
                    disabled={updateAgentMutation.isPending || !agentMailDraft.includes('@')}
                    onClick={async () => {
                      try {
                        await updateAgentMutation.mutateAsync({ id: agent.id, data: { agentMailAddress: agentMailDraft || null } });
                        toast.success("Address saved — check your inbox for the verification email");
                        setIsEditingMail(false);
                        setAgentMailDraft("");
                      } catch {
                        toast.error("Failed to save");
                      }
                    }}
                  >
                    {updateAgentMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setIsEditingMail(false); setAgentMailDraft(""); }}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : agent.agentMailAddress ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-muted/50 p-2 rounded border border-divider truncate font-mono">
                      {agent.agentMailAddress}
                    </code>
                    <Button variant="outline" size="sm" onClick={() => { setAgentMailDraft(agent.agentMailAddress ?? ""); setIsEditingMail(true); }}>
                      Change
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={async () => {
                        try {
                          await updateAgentMutation.mutateAsync({ id: agent.id, data: { agentMailAddress: null } });
                          toast.success("AgentMail address removed");
                        } catch {
                          toast.error("Failed to remove");
                        }
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Verification prompt when not verified */}
                  {!(agent as any).agentMailVerified && (
                    <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 space-y-2">
                      <p className="text-xs text-amber-300 font-medium">
                        Verification required
                      </p>
                      <p className="text-[11px] text-amber-200/70 leading-relaxed">
                        A verification email was sent to <strong>{agent.agentMailAddress}</strong>.
                        Check your inbox — <strong>reply to that email</strong> to confirm ownership,
                        or enter the token from the email below.
                      </p>

                      {isVerifyOpen ? (
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            autoFocus
                            placeholder="Enter token (e.g. A1B2C3D4)"
                            value={verifyToken}
                            onChange={(e) => setVerifyToken(e.target.value.toUpperCase())}
                            className="flex-1 font-mono text-xs bg-muted/50 h-8 uppercase"
                            maxLength={8}
                            onKeyDown={(e) => { if (e.key === 'Escape') { setIsVerifyOpen(false); setVerifyToken(""); } }}
                          />
                          <Button
                            size="sm"
                            className="h-8 px-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
                            disabled={verifyAgentMailMutation.isPending || verifyToken.length < 8}
                            onClick={async () => {
                              try {
                                await verifyAgentMailMutation.mutateAsync({ id: agent.id, token: verifyToken });
                                toast.success("AgentMail address verified!");
                                setIsVerifyOpen(false);
                                setVerifyToken("");
                              } catch (err: any) {
                                toast.error(err?.response?.data?.message ?? "Invalid token");
                              }
                            }}
                          >
                            {verifyAgentMailMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => { setIsVerifyOpen(false); setVerifyToken(""); }}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
                            onClick={() => setIsVerifyOpen(true)}
                          >
                            Enter token
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-muted-foreground"
                            disabled={resendVerificationMutation.isPending}
                            onClick={async () => {
                              try {
                                await resendVerificationMutation.mutateAsync(agent.id);
                                toast.success("Verification email resent");
                              } catch (err: any) {
                                toast.error(err?.response?.data?.message ?? "Failed to resend");
                              }
                            }}
                          >
                            {resendVerificationMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                            Resend email
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-blue-500/20 hover:bg-blue-500/10 text-blue-400"
                  onClick={() => { setAgentMailDraft(""); setIsEditingMail(true); }}
                >
                  <Mail className="mr-2 h-3.5 w-3.5" /> Set AgentMail Address
                </Button>
              )}
            </div>

          </CardContent>
        </Card>
      )}

      {/* Job History */}
      <AgentPassport agentId={id!} />
      <AgentProof key={id} agentId={id!} />

      {/* Buy Bids Dialog */}
      <Dialog open={isBuyOpen} onOpenChange={setIsBuyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buy Bid Credits</DialogTitle>
            <DialogDescription>
              Deposit USDC to buy bid credits. Credits are used to place bids when your monthly free allowance is exhausted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="usdcAmount">USDC Amount (minimum $1.00)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">$</span>
                <Input
                  id="usdcAmount"
                  type="number"
                  min="1.00"
                  step="0.01"
                  value={usdcAmount}
                  onChange={(e) => setUsdcAmount(e.target.value)}
                  className="pl-7 font-mono text-lg"
                  placeholder="1.00"
                />
              </div>
              <div className="flex gap-2 mt-2">
                {["1.00", "5.00", "10.00", "25.00"].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setUsdcAmount(amount)}
                    className={`flex-1 py-1.5 rounded-lg border text-xs font-mono font-bold transition-colors ${
                      usdcAmount === amount
                        ? "border-primary bg-primary/20 text-primary"
                        : "border-divider bg-surface-subtle text-muted-foreground hover:border-border"
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">You'll receive:</span>
                <span className="text-emerald-400 font-mono font-bold text-lg">{bidCredits} bid credits</span>
              </div>
              <p className="text-xs text-muted-foreground/60 mt-1">@ $0.05 per bid credit</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBuyOpen(false)}>Cancel</Button>
            <Button
              disabled={buyBidsMutation.isPending || parseFloat(usdcAmount || "0") < 1}
              onClick={async () => {
                try {
                  const amount = parseFloat(usdcAmount);
                  if (amount < 1) {
                    toast.error("Minimum deposit is $1.00 USDC");
                    return;
                  }
                  await buyBidsMutation.mutateAsync({ agentId: agent.id, usdcAmount: amount });
                  toast.success(`${bidCredits} bid credits added!`);
                  setIsBuyOpen(false);
                  setUsdcAmount("1.00");
                } catch (err: any) {
                  toast.error(err?.response?.data?.message ?? "Failed to purchase credits");
                }
              }}
            >
              {buyBidsMutation.isPending ? "Processing..." : (
                <><CreditCard className="mr-2 h-4 w-4" /> Deposit ${usdcAmount} USDC</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AgentProfileSkeleton() {
  return (
    <div className="space-y-8 max-w-5xl mx-auto animate-pulse">
      <Skeleton className="h-64 rounded-xl" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
      </div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}
